import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Package,
  CircleNotch,
  MagnifyingGlass,
  Plus,
  Minus,
  ArrowUp,
  ArrowDown,
  Eye,
  Warning,
  ChartLine,
  CurrencyDollar,
  Cube,
  Clock,
  Funnel,
  PencilSimple,
  Trash,
  Buildings,
  Truck,
  FileText
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import DocSummary from '../../components/DocSummary';
import ReportToolbar from '../../components/ReportToolbar';
import WarehousesTab from './inventory/WarehousesTab';
import StockTransfersTab from './inventory/StockTransfersTab';
import BinCardModal from './inventory/BinCardModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InventoryPage() {
  const { token, hasPermission } = useAuth();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventory, setInventory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [filterMovementType, setFilterMovementType] = useState('all');
  // Date range filter for the Stock Movements tab (drives both the visible
  // table filter and the universal PDF / Excel export URL).
  const [movStartDate, setMovStartDate] = useState('');
  const [movEndDate, setMovEndDate] = useState('');
  const [movSearch, setMovSearch] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 0,
    unit_cost: 0,
    min_stock_level: 10,
    valuation_method: 'average'
  });
  
  const [adjustment, setAdjustment] = useState({
    type: 'add',
    quantity: 0,
    reason: '',
    warehouse_id: '',
    bin_location: '',
  });
  // Warehouses list (for the adjustment modal's warehouse dropdown)
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  // Bin card viewer state
  const [binCardItem, setBinCardItem] = useState(null);

  const canCreate = hasPermission('hr', 'create');
  const canEdit = hasPermission('hr', 'edit');
  const canDelete = hasPermission('hr', 'delete');
  
  // Edit/Delete states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [invRes, movRes, statsRes, whRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory`, { headers }),
        axios.get(`${API_URL}/api/stock-movements`, { headers }),
        axios.get(`${API_URL}/api/inventory/stats`, { headers }),
        axios.get(`${API_URL}/api/inventory/warehouses`, { headers }),
      ]);
      
      setInventory(invRes.data);
      setMovements(movRes.data);
      setStats(statsRes.data);
      setWarehouseOptions(whRes.data || []);
    } catch (error) {
      console.error('Failed to fetch inventory data:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateItem = async () => {
    if (!newItem.description) {
      toast.error('Description is required');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/inventory`, null, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: newItem
      });
      toast.success('Item created');
      setShowAddModal(false);
      setNewItem({ description: '', quantity: 0, unit_cost: 0, min_stock_level: 10, valuation_method: 'average' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    }
  };

  const handleAdjustStock = async () => {
    if (!selectedItem || adjustment.quantity <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/inventory/${selectedItem.id}/adjust`, null, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          adjustment_type: adjustment.type,
          quantity: adjustment.quantity,
          reason: adjustment.reason || null,
          warehouse_id: adjustment.warehouse_id || null,
          bin_location: adjustment.bin_location || null,
        }
      });
      toast.success('Stock adjusted');
      setShowAdjustModal(false);
      setAdjustment({ type: 'add', quantity: 0, reason: '', warehouse_id: '', bin_location: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    }
  };

  const handleViewItem = async (item) => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/${item.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedItem(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load item details');
    }
  };

  const openAdjustModal = (item) => {
    setSelectedItem(item);
    const defaultWh = warehouseOptions.find(w => w.is_default)?.id || (warehouseOptions[0]?.id || '');
    setAdjustment({ type: 'add', quantity: 0, reason: '', warehouse_id: defaultWh, bin_location: '' });
    setShowAdjustModal(true);
  };

  // Edit item handler
  const handleEditItem = (item) => {
    setEditItem({
      id: item.id,
      description: item.description,
      min_stock_level: item.min_stock_level || 10,
      valuation_method: item.valuation_method || 'average'
    });
    setShowEditModal(true);
  };

  const handleUpdateItem = async () => {
    if (!editItem) return;
    try {
      await axios.patch(`${API_URL}/api/inventory/${editItem.id}`, null, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          min_stock_level: editItem.min_stock_level,
          valuation_method: editItem.valuation_method
        }
      });
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setEditItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  // Delete item handler
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/inventory/${itemToDelete.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Item deleted successfully');
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  // Filter inventory
  const filteredInventory = inventory.filter(item => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!item.description?.toLowerCase().includes(term)) return false;
    }
    if (filterLowStock && item.quantity >= (item.min_stock_level || 10)) return false;
    return true;
  });

  // Filter movements
  const filteredMovements = movements.filter(m => {
    const term = (movSearch || searchTerm || '').toLowerCase();
    if (term) {
      if (!m.item_description?.toLowerCase().includes(term) &&
          !m.reference_number?.toLowerCase().includes(term)) return false;
    }
    if (filterMovementType !== 'all' && m.movement_type !== filterMovementType) return false;
    // Client-side date filter (keeps the visible table in sync with the export URL)
    const ts = (m.created_at || '').slice(0, 10);
    if (movStartDate && ts && ts < movStartDate) return false;
    if (movEndDate && ts && ts > movEndDate) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="inventory-loading">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Package size={28} weight="duotone" className="text-primary" />
            Inventory & Stock
          </h1>
          <p className="text-muted-foreground mt-1">Track stock levels, values, and movements</p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowAddModal(true)} data-testid="add-item-btn">
            <Plus size={16} className="mr-2" />
            Add Item
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-tactile rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Cube size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Items</p>
              <p className="text-xl font-bold">{stats?.total_items || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="card-tactile rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <Package size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Qty</p>
              <p className="text-xl font-bold">{stats?.total_quantity?.toLocaleString() || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactile rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-emerald-500/10">
              <CurrencyDollar size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Value</p>
              <p className="text-xl font-bold">{formatCurrency(stats?.total_value || 0)}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactile rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-500/10">
              <Warning size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Low Stock</p>
              <p className="text-xl font-bold text-amber-600">{stats?.low_stock_count || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-500/10">
              <Package size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Out of Stock</p>
              <p className="text-xl font-bold text-red-600">{stats?.out_of_stock || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-tactile rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-purple-500/10">
              <ChartLine size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">30-Day Moves</p>
              <p className="text-xl font-bold">{stats?.recent_movements || 0}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search items, reference numbers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="inventory-search"
          />
        </div>
        <Button
          variant={filterLowStock ? "default" : "outline"}
          onClick={() => setFilterLowStock(!filterLowStock)}
          className="gap-2"
        >
          <Warning size={16} />
          Low Stock Only
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package size={16} className="mr-2" />
            Inventory ({filteredInventory.length})
          </TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">
            <ChartLine size={16} className="mr-2" />
            Stock Movements ({filteredMovements.length})
          </TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="tab-warehouses">
            <Buildings size={16} className="mr-2" />
            Warehouses
          </TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">
            <Truck size={16} className="mr-2" />
            Stock Transfers
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-4">
          <DocSummary kind="inventory_items" label="Inventory Items" filters={{ q: searchTerm }} />
          <ReportToolbar
            testidPrefix="inventory-toolbar"
            downloadBaseName="inventory-list"
            exportUrl={(fmt) => {
              const params = new URLSearchParams();
              if (searchTerm) params.set('q', searchTerm);
              if (filterLowStock) params.set('low_stock', 'true');
              return `${API_URL}/api/reports/inventory.${fmt}${params.toString() ? '?' + params.toString() : ''}`;
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                  <TableHead>Valuation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInventory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No inventory items found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInventory.map(item => {
                    const isLowStock = item.quantity < (item.min_stock_level || 10);
                    const isOutOfStock = item.quantity <= 0;
                    
                    return (
                      <TableRow key={item.id} data-testid={`inv-row-${item.id}`}>
                        <TableCell className="font-medium">{item.description}</TableCell>
                        <TableCell className="text-right font-mono">
                          {item.quantity?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_cost || 0)}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(item.total_value || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {item.valuation_method || 'average'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isOutOfStock ? (
                            <Badge className="bg-red-500/10 text-red-600">Out of Stock</Badge>
                          ) : isLowStock ? (
                            <Badge className="bg-amber-500/10 text-amber-600">Low Stock</Badge>
                          ) : (
                            <Badge className="bg-emerald-500/10 text-emerald-600">In Stock</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end">
                            <RowActionsMenu
                              testid={`item-${item.id}`}
                              actions={[
                                { id: 'view', label: 'View Details', icon: Eye, onClick: () => handleViewItem(item) },
                                { id: 'bin-card', label: 'View Bin Card', icon: FileText, onClick: () => setBinCardItem(item.description) },
                                canEdit && { id: 'edit', label: 'Edit Item', icon: PencilSimple, onClick: () => handleEditItem(item) },
                                canEdit && { id: 'adjust', label: 'Adjust Stock', icon: Plus, onClick: () => openAdjustModal(item) },
                                canDelete && 'separator',
                                canDelete && { id: 'delete', label: 'Delete', icon: Trash, onClick: () => handleDeleteClick(item), destructive: true },
                              ].filter(Boolean)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </motion.div>
        </TabsContent>

        {/* Stock Movements Tab */}
        <TabsContent value="movements" className="mt-4">
          <ReportToolbar
            testidPrefix="movements-toolbar"
            downloadBaseName="stock-movements"
            search={movSearch}
            onSearch={setMovSearch}
            searchPlaceholder="Search item or reference…"
            startDate={movStartDate}
            endDate={movEndDate}
            onStartDate={setMovStartDate}
            onEndDate={setMovEndDate}
            exportUrl={(fmt) => {
              const params = new URLSearchParams();
              if (movSearch) params.set('item', movSearch);
              if (filterMovementType && filterMovementType !== 'all') params.set('movement_type', filterMovementType);
              if (movStartDate) params.set('start_date', movStartDate);
              if (movEndDate) params.set('end_date', movEndDate);
              return `${API_URL}/api/reports/stock-movements.${fmt}${params.toString() ? '?' + params.toString() : ''}`;
            }}
            extraFilters={(
              <Select value={filterMovementType} onValueChange={setFilterMovementType}>
                <SelectTrigger className="w-32 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="in">Stock In</SelectItem>
                  <SelectItem value="out">Stock Out</SelectItem>
                </SelectContent>
              </Select>
            )}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMovements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No stock movements found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMovements.map(movement => (
                    <TableRow key={movement.id} data-testid={`mov-row-${movement.id}`}>
                      <TableCell className="text-sm">
                        {new Date(movement.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{movement.item_description}</TableCell>
                      <TableCell>
                        {movement.movement_type === 'in' ? (
                          <Badge className="bg-emerald-500/10 text-emerald-600">
                            <ArrowDown size={12} className="mr-1" />
                            In
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-600">
                            <ArrowUp size={12} className="mr-1" />
                            Out
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{movement.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(movement.unit_cost || 0)}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(movement.total_value || 0)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{movement.reference_number}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {movement.reference_type}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </motion.div>
        </TabsContent>

        {/* Warehouses Tab */}
        <TabsContent value="warehouses" className="mt-4">
          <WarehousesTab />
        </TabsContent>

        {/* Stock Transfers Tab */}
        <TabsContent value="transfers" className="mt-4">
          <StockTransfersTab />
        </TabsContent>
      </Tabs>

      {/* Bin Card Modal */}
      <BinCardModal
        open={!!binCardItem}
        onClose={() => setBinCardItem(null)}
        itemDescription={binCardItem || ''}
      />

      {/* Add Item Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Inventory Item</DialogTitle>
            <DialogDescription>Create a new item in your inventory</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Description *</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Item description"
                data-testid="new-item-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Initial Quantity</Label>
                <Input
                  type="number"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div>
                <Label>Unit Cost</Label>
                <Input
                  type="number"
                  value={newItem.unit_cost}
                  onChange={(e) => setNewItem({ ...newItem, unit_cost: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Stock Level</Label>
                <Input
                  type="number"
                  value={newItem.min_stock_level}
                  onChange={(e) => setNewItem({ ...newItem, min_stock_level: parseFloat(e.target.value) || 10 })}
                  min="0"
                />
              </div>
              <div>
                <Label>Valuation Method</Label>
                <Select
                  value={newItem.valuation_method}
                  onValueChange={(v) => setNewItem({ ...newItem, valuation_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="average">Average Cost</SelectItem>
                    <SelectItem value="fifo">FIFO</SelectItem>
                    <SelectItem value="lifo">LIFO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleCreateItem} data-testid="create-item-btn">Create Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              {selectedItem?.description} - Current: {selectedItem?.quantity}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Adjustment Type</Label>
              <Select value={adjustment.type} onValueChange={(v) => setAdjustment({ ...adjustment, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <div className="flex items-center gap-2">
                      <Plus size={14} className="text-emerald-600" />
                      Add Stock
                    </div>
                  </SelectItem>
                  <SelectItem value="remove">
                    <div className="flex items-center gap-2">
                      <Minus size={14} className="text-red-600" />
                      Remove Stock
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Warehouse picker — required so multi-store stock stays accurate */}
            <div>
              <Label>Warehouse *</Label>
              <Select
                value={adjustment.warehouse_id || ''}
                onValueChange={(v) => setAdjustment({ ...adjustment, warehouse_id: v })}
              >
                <SelectTrigger data-testid="adjust-warehouse"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouseOptions.filter(w => w.is_active).map(w => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.code} · {w.name}{w.is_default ? ' (Default)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bin / Rack (optional)</Label>
              <Input
                value={adjustment.bin_location}
                onChange={(e) => setAdjustment({ ...adjustment, bin_location: e.target.value })}
                placeholder="e.g. Rack A · Shelf 3"
                data-testid="adjust-bin"
              />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={adjustment.quantity}
                onChange={(e) => setAdjustment({ ...adjustment, quantity: parseFloat(e.target.value) || 0 })}
                min="0"
                data-testid="adjust-qty"
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={adjustment.reason}
                onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                placeholder="e.g., Damaged, Correction, Returns..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
            <Button onClick={handleAdjustStock} data-testid="adjust-stock-btn">
              {adjustment.type === 'add' ? 'Add Stock' : 'Remove Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedItem?.description}</DialogTitle>
            <DialogDescription>Item details and movement history</DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="text-2xl font-bold">{selectedItem.quantity}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Unit Cost</p>
                  <p className="text-2xl font-bold">{formatCurrency(selectedItem.unit_cost || 0)}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Total Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(selectedItem.total_value || 0)}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Valuation</p>
                  <p className="text-lg font-bold capitalize">{selectedItem.valuation_method}</p>
                </div>
              </div>

              {/* Recent Movements */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Clock size={18} />
                  Recent Movements
                </h4>
                <div className="max-h-60 overflow-y-auto">
                  {selectedItem.movements?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedItem.movements.map(m => (
                          <TableRow key={m.id}>
                            <TableCell className="text-sm">
                              {new Date(m.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {m.movement_type === 'in' ? (
                                <Badge className="bg-emerald-500/10 text-emerald-600">In</Badge>
                              ) : (
                                <Badge className="bg-red-500/10 text-red-600">Out</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{m.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(m.total_value)}</TableCell>
                            <TableCell className="font-mono text-sm">{m.reference_number}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">No movements recorded</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
            {canEdit && (
              <Button onClick={() => { setShowDetailModal(false); openAdjustModal(selectedItem); }}>
                Adjust Stock
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Inventory Item</DialogTitle>
            <DialogDescription>
              Update item settings for {editItem?.description}
            </DialogDescription>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div>
                <Label>Description</Label>
                <Input
                  value={editItem.description}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">Description cannot be changed</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Stock Level</Label>
                  <Input
                    type="number"
                    value={editItem.min_stock_level}
                    onChange={(e) => setEditItem({ ...editItem, min_stock_level: parseFloat(e.target.value) || 0 })}
                    min="0"
                    data-testid="edit-min-stock"
                  />
                </div>
                <div>
                  <Label>Valuation Method</Label>
                  <Select
                    value={editItem.valuation_method}
                    onValueChange={(v) => setEditItem({ ...editItem, valuation_method: v })}
                  >
                    <SelectTrigger data-testid="edit-valuation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="average">Average Cost</SelectItem>
                      <SelectItem value="fifo">FIFO</SelectItem>
                      <SelectItem value="lifo">LIFO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateItem} data-testid="update-item-btn">Update Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash className="w-5 h-5" />
              Delete Inventory Item
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{itemToDelete?.description}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-amber-500/10 rounded-lg text-amber-700 text-sm">
            <Warning size={16} className="inline mr-2" />
            Items with existing stock movements cannot be deleted.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteItem}
              data-testid="confirm-delete-item"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
